<template>
    <div class="flex gap-4 flex-col sm:flex-row">
        <!-- Count Input for count-based benefits -->
        <div class="flex-1" v-if="isCountBased">
            <UiInput
                id="usage-count"
                :label="'Quantity used'"
                v-model="model.count"
                type="number"
                step="1"
                min="0"
                placeholder="0"
                :options="{
                    hideLabel: hideLabels,
                }"
                class="flex-1"
                :error="errors['usageCount']"
            >
                <template #input>
                    <div class="relative">
                        <input
                            v-model="model.count"
                            type="number"
                            inputmode="numeric"
                            step="1"
                            min="0"
                            placeholder="0"
                            class="block w-full py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-sm font-normal text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:ring-0 focus:border-slate-800 dark:focus:border-slate-400 hover:border-slate-800 dark:hover:border-slate-400 transition-colors font-['Inter']"
                            :class="showMaxCountButton ? 'pl-3 pr-12' : 'px-3'"
                        />
                        <button
                            v-if="showMaxCountButton"
                            type="button"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-orange hover:text-orange-dark transition-colors"
                            @click.stop="fillMaxCount"
                        >
                            Max
                        </button>
                    </div>
                </template>
                <template #hint>
                    <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 sm:hidden inline">
                        {{ props.countRemainingMessage }}
                    </p>
                </template>
            </UiInput>
        </div>

        <!-- Amount Input with Dollar Sign for monetary benefits -->
        <div class="flex-1" v-else>
            <UiInput
                id="redemption-amount"
                label="Redeemed"
                v-model="model.amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                :options="{
                    icon: 'dollar-sign',
                    inputClass: 'pl-7',
                    hideLabel: hideLabels,
                }"
                class="flex-1"
                :error="errors['usageAmount']"
            >
                <template #input>
                    <div class="relative">
                        <div
                            class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                        >
                            <span class="text-slate-500 dark:text-slate-400 text-sm">$</span>
                        </div>
                        <input
                            v-model="model.amount"
                            type="number"
                            inputmode="decimal"
                            step="1.00"
                            min="0"
                            placeholder="0.00"
                            class="block w-full pl-7 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-sm font-normal text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:ring-0 focus:border-slate-800 dark:focus:border-slate-400 hover:border-slate-800 dark:hover:border-slate-400 transition-colors font-['Inter']"
                            :class="showMaxAmountButton ? 'pr-12' : 'pr-3'"
                        />
                        <button
                            v-if="showMaxAmountButton"
                            type="button"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-orange hover:text-orange-dark transition-colors"
                            @click.stop="fillMaxAmount"
                        >
                            Max
                        </button>
                    </div>
                </template>
                <template #hint>
                    <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 sm:hidden inline">
                        {{ props.amountRemainingMessage }}
                    </p>
                </template>
            </UiInput>
        </div>
        <div class="flex-1">
            <UiInput
                id="redemption-date"
                label="Date"
                :options="{
                    icon: 'calendar',
                    fakeLabelSpace: false,
                    hideLabel: hideLabels,
                }"
                class="flex-1"
                :error="errors['usageDate']"
            >
                <template #input>
                    <div class="relative">
                        <input
                            v-model="formattedDate"
                            type="date"
                            class="date-input block w-full pl-10 pr-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:ring-0 focus:border-slate-800 dark:focus:border-slate-400 hover:border-slate-800 dark:hover:border-slate-400 transition-colors"
                            :max="dayjs().format('YYYY-MM-DD')"
                        />
                    </div>
                </template>
            </UiInput>
        </div>
    </div>

    <!-- Notes expandable section -->
    <div class="mt-3">
        <button
            v-if="!showNotes"
            type="button"
            class="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            @click="showNotes = true"
        >
            <Icon icon="plus" :width="16" :height="16" />
            Add Note
        </button>
        <div v-else>
            <label
                class="block text-slate-600 dark:text-slate-400 text-sm font-medium leading-tight mb-1.5"
            >
                Note
            </label>
            <textarea
                v-model="model.notes"
                placeholder="Any notes and details on this benefit usage"
                rows="3"
                maxlength="1000"
                class="block w-full px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-sm font-normal text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:ring-0 focus:border-slate-800 dark:focus:border-slate-400 hover:border-slate-800 dark:hover:border-slate-400 transition-colors resize-none font-['Inter']"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Icon from '@/components/ui/icon/icon.vue';
import dayjs from 'dayjs';

const props = defineProps({
    hideLabels: {
        type: Boolean,
        default: false,
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
    amountRemainingMessage: {
        type: String,
        default: '',
    },
    countRemainingMessage: {
        type: String,
        default: '',
    },
    isCountBased: {
        type: Boolean,
        default: false,
    },
    countLabel: {
        type: String,
        default: '',
    },
    maxAmount: {
        type: [Number, String],
        default: null,
    },
    maxCount: {
        type: [Number, String],
        default: null,
    },
});

const model = defineModel({
    type: Object,
    default: () => ({
        amount: '',
        count: 1,
        date: dayjs().format('YYYY-MM-DD'),
        notes: '',
    }),
});

const showNotes = ref(false);

// "Max" pill: fills the amount/count input with the largest value the user is
// allowed to enter for this period. Only shown when the parent passes a
// positive, finite cap (nothing remaining => no button).
const showMaxAmountButton = computed(() => {
    const max = parseFloat(props.maxAmount);
    return Number.isFinite(max) && max > 0;
});

const showMaxCountButton = computed(() => {
    const max = parseFloat(props.maxCount);
    return Number.isFinite(max) && max > 0;
});

const fillMaxAmount = () => {
    model.value.amount = Number(parseFloat(props.maxAmount).toFixed(2));
};

const fillMaxCount = () => {
    model.value.count = parseFloat(props.maxCount);
};

// Sync showNotes with the model object reference so it resets whenever the
// parent replaces the whole model (e.g. on modal close), not just when notes changes.
watch(
    model,
    (val) => {
        showNotes.value = !!val?.notes;
    },
    { immediate: true }
);

const formattedDate = computed({
    get() {
        return model.value.date ? model.value.date.split('T')[0] : '';
    },
    set(value) {
        model.value.date = value;
    },
});
</script>

<style scoped>
/* Custom styling for native date input to look more like FlatPickr */
.date-input {
    color-scheme: light;
}
:where(.dark) .date-input {
    color-scheme: dark;
}

/* Hide the default calendar icon and use our custom one */
.date-input::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
}

/* Style the date input text */
.date-input::-webkit-datetime-edit {
    color: #374151;
}
:where(.dark) .date-input::-webkit-datetime-edit {
    color: #e2e8f0;
}

.date-input::-webkit-datetime-edit-fields-wrapper {
    padding: 0;
}

.date-input::-webkit-datetime-edit-text {
    color: #6b7280;
    padding: 0 2px;
}
:where(.dark) .date-input::-webkit-datetime-edit-text {
    color: #94a3b8;
}

.date-input::-webkit-datetime-edit-month-field,
.date-input::-webkit-datetime-edit-day-field,
.date-input::-webkit-datetime-edit-year-field {
    color: #374151;
}
:where(.dark) .date-input::-webkit-datetime-edit-month-field,
:where(.dark) .date-input::-webkit-datetime-edit-day-field,
:where(.dark) .date-input::-webkit-datetime-edit-year-field {
    color: #f1f5f9;
}

/* When empty, show placeholder-like styling */
.date-input:invalid {
    color: #9ca3af;
}

/* Focus states */
.date-input:focus::-webkit-datetime-edit {
    color: #111827;
}
:where(.dark) .date-input:focus::-webkit-datetime-edit {
    color: #f8fafc;
}
</style>
